<template>
  <div
    class="side-bar"
    :class="{ 'side-bar__shrunk': isShrunk }"
  >
    <div class="side-bar__header">
      <template v-show="!isShrunk">
        <DarkLogo />
      </template>
      <gl-icon
        :class="{'sidebar-open-icon__shrunk': isShrunk}"
        height="24"
        name="sidebar-open"
        width="24"
        @click="setShrunk(!isShrunk)"
      />
    </div>
    <div class="flex column space-between side-bar__main--wrap">
      <div class="side-bar__main">
        <div
          v-if="userData && userData.role === 'admin'"
          v-tooltip.right="{ content: 'Admin Panel', visible: isShrunk }"
          class="side-bar__nav-link"
          :class="[{'side-bar__has-open-sub-item': isAdminSubMenuOpen}, {'side-bar__nav-link--shrunk': isShrunk}]"
          @click="openSubMenu"
        >
          <div class="relative">
            <gl-icon
              height="24"
              name="admin-panel"
              width="24"
            />
            <div
              v-if="(hasUntrackedTags || hasUntrackedType) && isShrunk"
              class="warning-noty mt-1 ml-3"
              style="position: absolute; top: -4px; right: -2px"
            />
          </div>
          <div class="fullwidth flex align-center space-between">
            <span
              v-show="!isShrunk"
              class="side-bar__nav-link--title"
            >
              Admin Panel
            </span>
            <div
              v-if="(hasUntrackedTags || hasUntrackedType || hasNotify('labeling')) && !isAdminSubMenuOpen"
              v-tooltip.top="{ content: untrackedLabelsInfo }"
              class="warning-noty mt-1 ml-1"
            />
            <gl-icon
              :class="`gl-user-dropdown__arrow-icon--${isAdminSubMenuOpen ? 'opened' : 'closed'}`"
              :height="24"
              name="arrow-down"
              :width="24"
            />
          </div>
        </div>
        <transition name="fade">
          <div
            class="sub-menu__wrap"
            :class="{'sub-menu__open': isAdminSubMenuOpen && !isShrunk}"
          >
            <router-link
              active-class="side-bar__nav-link--active"
              class="side-bar__nav-link side-bar__sub-nav-link"
              :class="{'side-bar__nav-link--shrunk': isShrunk}"
              to="/users"
            >
              <gl-icon
                :height="24"
                name="users-list"
                :width="24"
              />
              <span class="side-bar__nav-link--title">
                Users
              </span>
            </router-link>
            <router-link
              :active-class="$route.name === 'labeling' ? 'side-bar__nav-link--active' : ''"
              class="side-bar__nav-link side-bar__sub-nav-link"
              :class="{'side-bar__nav-link--shrunk': isShrunk}"
              :to="{ path: 'labeling', query: { tab: 'labeling' }}"
            >
              <gl-icon
                height="24"
                name="tagging"
                width="24"
              />
              <span class="side-bar__nav-link--title">
                Labeling
              </span>
              <div
                v-if="hasNotify('labeling') && isAdminSubMenuOpen"
                v-tooltip.top="{ content: 'Unresolved conflicts' }"
                class="warning-noty mt-1 ml-3"
              />
            </router-link>
            <router-link
              active-class="side-bar__nav-link--active"
              class="side-bar__nav-link side-bar__sub-nav-link"
              :class="{'side-bar__nav-link--shrunk': isShrunk}"
              :to="{ path: 'scoring'}"
            >
              <gl-icon
                :height="24"
                name="scoring"
                :width="24"
              />
              <span
                v-show="!isShrunk"
                class="side-bar__nav-link--title"
              >
                Scoring
              </span>
              <div
                v-if="hasNotify('scoring') && isAdminSubMenuOpen"
                v-tooltip.top="{ content: untrackedLabelsInfo }"
                class="warning-noty mt-1 ml-3"
              />
            </router-link>
          </div>
        </transition>
      </div>
      <div class="side-bar__footer">
        <div
          v-tooltip.right="{ content: 'Contact Support', visible: isShrunk }"
          class="side-bar__nav-link"
          :class="{'side-bar__nav-link--shrunk': isShrunk}"
          @click="contactSupport"
        >
          <gl-icon
            :height="24"
            name="pods"
            :width="24"
          />
          <span
            v-show="!isShrunk"
            class="side-bar__nav-link--title"
          >
            Contact Support
          </span>
        </div>
        <div class="flex space-between align-center">
          <router-link
            v-tooltip.right="{ content: 'My Account', visible: isShrunk }"
            active-class="side-bar__nav-link--active"
            class="side-bar__nav-link"
            :class="{'side-bar__nav-link--shrunk': isShrunk}"
            :to="{ path: 'profile'}"
          >
            <div
              v-if="alarmActiveTo(userData.activeTo)"
              class="alarm-active-date"
            />
            <gl-icon
              :height="24"
              name="my-profile"
              :width="24"
            />
            <span
              v-show="!isShrunk"
              class="side-bar__nav-link--title"
            >
              My Account
            </span>
          </router-link>
          <gl-icon
            class="mr-4 ml-3 pointer"
            :height="24"
            name="logout"
            :width="24"
            @click="logoutUser"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import GlIcon from '@/components/gl-icon'
import DarkLogo from '@/assets/svg/header/GLNewLogo.svg?inline'
// Vuex
import {mapActions, mapState, mapMutations} from "vuex";
// Utils
import { alarmActiveTo } from "@/utils/profile";

export default {
  name: 'SidepanelMenu',

  components: {
    GlIcon,
    DarkLogo,
  },

  data() {
    return {
      isAdminSubMenuOpen: false,
      notifyList: {},
      untrackedLabelsInfo: 'Unscored types / tags'
    }
  },
  computed: {
    ...mapState('sidebar', ['isShrunk', 'hasUntrackedTags', 'hasUntrackedType']),
    ...mapState('user', ['userData', 'token']),
    adminPages() {
      return ['users','labeling', 'scoring', 'statistics'].includes(this.$route.name)
    },
  },
  watch: {
    $route: {
      async handler(val) {
        this.isAdminSubMenuOpen = !!(val && this.adminPages);

        if (this.token) {
          this.getNotificationsCount().then(({ data }) => {
            this.notifyList = data || {}
          })
        }
      },
      immediate: true,
    },
    '$store.state.analytics.searchValue': 'setSearchValue',
  },
  methods: {
    alarmActiveTo,
    ...mapActions('user', ['logout']),
    ...mapActions('sidebar', ['getNotificationsCount']),
    ...mapMutations({
      setShrunk: 'sidebar/SET_SHRUNK'
    }),
    hasNotify(entity) {
      return this.notifyList[entity] > 0
    },
    setSearchValue(val) {
      this.search = val
    },
    openSubMenu() {
      if (this.isShrunk) {
        this.setShrunk(false)
        this.isAdminSubMenuOpen = true
        return
      }

      this.isAdminSubMenuOpen = !this.isAdminSubMenuOpen
    },
    contactSupport() {
      window.location.href = 'mailto:support@glprotocol.com'
    },
    logoutUser() {
      localStorage.removeItem('caseId')
      this.logout();
      window.location.assign('/');
    },
  }
};
</script>

<style scoped>
.side-bar {
  width: 230px;
  height: 100%;
  padding: 0 0 15px;
  transition: width 0.2s;
  overflow: hidden;
  background: #fff;
  box-shadow: 14px 0 31px -9px rgba(0,0,0,0.46);
}

.side-bar__shrunk {
  width: 50px;
  transition: width 0.2s;
}

.side-bar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e3e3e3;
}

.side-bar__footer {
  border-top: 1px solid #e3e3e3;
}

.side-bar__nav-link {
  align-items: center;
  display: flex;
  padding: 10px 10px 10px 5px;
  color: var(--dark);
  font-weight: 500;
  height: 50px;
  width: 100%;
  line-height: 1.14;
  font-size: 14px;
  position: relative;
  text-decoration: none;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.5s ease-out;
}

.side-bar__nav-link:hover {
  background-color: rgba(90, 120, 234, 0.25);
}

.side-bar__nav-link svg {
  margin: 0 8px;
}

.sub-menu__wrap {
  max-height: 0;
  transition: max-height 0.4s;
  overflow: hidden;
}

.sub-menu__open {
  max-height: 500px;
  transition: max-height 0.4s;
  overflow: hidden;
}

.side-bar__nav-link--active {
  font-weight: 600;
  border-left: 4px solid var(--reflex-bluet);
}

.side-bar__sub-nav-link {
  padding-left: 30px;
}

.side-bar__has-open-sub-item {
  /*background: #e6e6e6;*/
}

.alarm-active-date {
  height: 10px;
  width: 10px;
  border-radius: 10px;
  background: red;
  position: absolute;
  top: 10px;
  left: 32px;
}

.side-bar__main--wrap {
  height: calc(100% - 41px);
  padding-bottom: 15px;
}

.side-bar__nav-link--shrunk {
  padding-left: 0;
}

.side-bar__nav-link--title {
  margin-top: 3px;
}

.sidebar-open-icon__shrunk {
  margin-left: -3px;
}
</style>
